import { usePostFrontRichiesteIdModuliIdSezioniIdFileCodice } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetParsedProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { useMemo } from 'react';
import { useFillableSubmissionContext } from '../../../FillableSubmissionContext.tsx';
import { type IUploadWidget } from '../IUploadWidget.ts';
import { useUploadWidgetFieldName } from './useUploadWidgetFieldName.ts';

interface IParams {
  widget: IUploadWidget;
  options: IWidgetParsedProps;
  /**
   * Se true, indica che l'input sarà per il file "pubblico" (direttiva omissis true),
   * quindi sostituisce i parametri con quelli della versione pubblica
   * (e.g. titolo, nome, label, help, ecc.)
   */
  publicUpload: boolean | null | undefined;
}

export function useUploadWidgetTitle(
  widget: IUploadWidget,
  publicUpload: boolean | null | undefined,
) {
  return useMemo(() => {
    if (publicUpload) {
      return widget.publicUploadTitle ? (
        <RichTextAdvancedRender text={widget.publicUploadTitle} />
      ) : undefined;
    }
    return widget.title ? (
      <RichTextAdvancedRender text={widget.title} />
    ) : undefined;
  }, [widget.title, widget.publicUploadTitle, publicUpload]);
}

export function getSectionIdFromFieldName(name: string) {
  return name.split('.')[0];
}

/** Data la lista di estensioni accettate, ritorna una lista di mimetype */
function getPermittedExtensions(
  fileTypes: string[] | null | undefined,
): string[] {
  if (!fileTypes) return [];
  return fileTypes
    .map(fileType => {
      if (fileType.startsWith('.')) {
        return fileType;
      }
      return `.${fileType}`;
    })
    .filter(Boolean);
}

const TO_MB = 1024 * 1024; // 1MB = 1024 KB = 1024*1024 B
/**
 * La dimensione massima di upload dei file non può superare i 100MB e deve essere sempre definita
 */
function getMaxFileSize(maxSize: number | null | undefined) {
  if (!maxSize) return 100 * TO_MB;
  return Math.min(maxSize, 100 * TO_MB);
}

/**
 * Hook che racchiude la logica per gestire l'upload di un file
 */
export function useUploadWidget(params: IParams) {
  const { widget, options, publicUpload } = params;

  const title = useUploadWidgetTitle(widget, publicUpload);

  const { fieldName, filenameFieldName } = useUploadWidgetFieldName({
    publicUpload,
    options,
  });

  const sectionId = getSectionIdFromFieldName(fieldName);
  const maxFileSize = getMaxFileSize(widget.rules?.maxDimension?.value);
  const maxFileSizeMessage: string | null =
    widget.rules?.maxDimension?.customMessage ?? null;

  const fileTypes: string[] = useMemo(
    () => getPermittedExtensions(widget.rules?.fileType?.value),
    [widget],
  );

  const fileTypesMessage: string | null =
    widget.rules?.fileType?.customMessage ?? null;

  const submissionContext = useFillableSubmissionContext();

  const upload = usePostFrontRichiesteIdModuliIdSezioniIdFileCodice({
    request: {
      headers: {
        ...(publicUpload ? { sensibile: true } : {}),
      },
    },
  });

  return {
    title,
    fieldName,
    sectionId,
    maxFileSize,
    maxFileSizeMessage,
    fileTypes,
    fileTypesMessage,
    submissionId: submissionContext.submissionId,
    submissionModuleId: submissionContext.submissionModuleId,
    upload,
    filenameFieldName,
  };
}
